import React, { useEffect, useState } from 'react';
import { Table, Button, Pagination, Modal, Form } from 'react-bootstrap';

// Base URL from .env
const API_URL = process.env.REACT_APP_API_URL;

// Interface for Testimonial
interface Testimonial {
  id: number;
  name: string;
  text: string;
  created_at: string;
  updated_at: string;
}

const TestimonialList: React.FC = () => {
  const [testimonials, setTestimonials] = useState<Testimonial[]>([]);
  const [editTestimonial, setEditTestimonial] = useState<Testimonial | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [newTestimonial, setNewTestimonial] = useState({ name: '', text: '' });
  const recordsPerPage = 10;

  // Fetch testimonials on component mount
  useEffect(() => {
    const fetchTestimonials = async () => {
      try {
        const token = localStorage.getItem('token');
        const res = await fetch(`${API_URL}/api/testimonials`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!res.ok) throw new Error('Failed to fetch testimonials');
        const data: Testimonial[] = await res.json();
        setTestimonials(data);
      } catch (err) {
        console.error('Error fetching testimonials:', err);
      }
    };
    fetchTestimonials();
  }, []);

  // Handle Delete
  const handleDelete = async (id: number) => {
    if (!window.confirm('Are you sure you want to delete this testimonial?')) return;

    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/api/testimonials/${id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error('Failed to delete testimonial');
      setTestimonials(testimonials.filter((testimonial) => testimonial.id !== id));
    } catch (err) {
      console.error('Error deleting testimonial:', err);
    }
  };

  // Handle Edit
  const handleEdit = (testimonial: Testimonial) => {
    setEditTestimonial(testimonial);
    setShowEditModal(true);
  };

  // Handle Update
  const handleUpdate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editTestimonial) return;

    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/api/testimonials/${editTestimonial.id}`, {
        method: 'PUT',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          name: editTestimonial.name,
          text: editTestimonial.text,
        }),
      });

      if (res.ok) {
        const updatedTestimonial = await res.json();
        setTestimonials(
          testimonials.map((testimonial) =>
            testimonial.id === editTestimonial.id ? { ...testimonial, ...updatedTestimonial } : testimonial
          )
        );
        setEditTestimonial(null);
        setShowEditModal(false);
      } else {
        console.error('Failed to update testimonial:', await res.json());
      }
    } catch (err) {
      console.error('Error updating testimonial:', err);
    }
  };

  // Handle Add Testimonial
  const handleAdd = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/api/testimonials`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(newTestimonial),
      });

      if (res.ok) {
        const response = await res.json();
        const addedTestimonial = response.testimonial; // Sahi object extract kiya
        setTestimonials([
          ...testimonials,
          {
            ...addedTestimonial,
            created_at: addedTestimonial.created_at || new Date().toISOString(),
            updated_at: addedTestimonial.updated_at || new Date().toISOString(),
          },
        ]);
        setNewTestimonial({ name: '', text: '' });
        setShowAddModal(false);
      } else {
        console.error('Failed to add testimonial:', await res.json());
      }
    } catch (err) {
      console.error('Error adding testimonial:', err);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    if (editTestimonial) {
      setEditTestimonial({ ...editTestimonial, [e.target.name]: e.target.value });
    }
  };

  const handleAddChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setNewTestimonial({ ...newTestimonial, [e.target.name]: e.target.value });
  };

  // Pagination Logic
  const indexOfLastRecord = currentPage * recordsPerPage;
  const indexOfFirstRecord = indexOfLastRecord - recordsPerPage;
  const currentRecords = testimonials.slice(indexOfFirstRecord, indexOfLastRecord);
  const totalPages = Math.ceil(testimonials.length / recordsPerPage);

  const paginate = (pageNumber: number) => setCurrentPage(pageNumber);

  return (
    <>
      <h2 className="dashboard-title text-center mb-5">Testimonial List</h2>
      
      <div className="text-end mb-3">
        <Button
          variant="success"
          className="modern-btn"
          onClick={() => setShowAddModal(true)}
        >
          <i className="fas fa-plus"></i> Add Testimonial
        </Button>
      </div>

      <Table striped bordered hover responsive className="modern-table">
        <thead>
          <tr>
            <th>ID</th>
            <th>Name</th>
            <th>Testimonial Text</th>
            <th>Created At</th>
            <th>Updated At</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {currentRecords.map((testimonial) => (
            <tr key={testimonial.id}>
              <td>{testimonial.id}</td>
              <td>{testimonial.name}</td>
              <td>{(testimonial.text || '').substring(0, 50)}...</td>
              <td>{new Date(testimonial.created_at).toLocaleDateString()}</td>
              <td>{new Date(testimonial.updated_at).toLocaleDateString()}</td>
              <td className="action-buttons">
                <Button
                  variant="warning"
                  size="sm"
                  className="me-2 modern-btn"
                  onClick={() => handleEdit(testimonial)}
                >
                  <i className="fas fa-edit"></i> Edit
                </Button>
                <Button
                  variant="danger"
                  size="sm"
                  className="modern-btn"
                  onClick={() => handleDelete(testimonial.id)}
                >
                  <i className="fas fa-trash-alt"></i> Delete
                </Button>
              </td>
            </tr>
          ))}
        </tbody>
      </Table>

      <Pagination className="justify-content-center mt-4">
        {Array.from({ length: totalPages }, (_, index) => (
          <Pagination.Item
            key={index + 1}
            active={index + 1 === currentPage}
            onClick={() => paginate(index + 1)}
          >
            {index + 1}
          </Pagination.Item>
        ))}
      </Pagination>

      {/* Add Testimonial Modal */}
      <Modal show={showAddModal} onHide={() => setShowAddModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Add Testimonial</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form onSubmit={handleAdd}>
            <Form.Group className="mb-3">
              <Form.Label>Name</Form.Label>
              <Form.Control
                type="text"
                name="name"
                value={newTestimonial.name}
                onChange={handleAddChange}
                required
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Testimonial Text</Form.Label>
              <Form.Control
                as="textarea"
                rows={5}
                name="text"
                value={newTestimonial.text}
                onChange={handleAddChange}
                required
              />
            </Form.Group>
            <div className="text-center">
              <Button variant="primary" type="submit" className="me-2 modern-btn">
                <i className="fas fa-check"></i> Add
              </Button>
              <Button variant="secondary" className="modern-btn" onClick={() => setShowAddModal(false)}>
                <i className="fas fa-times"></i> Cancel
              </Button>
            </div>
          </Form>
        </Modal.Body>
      </Modal>

      {/* Edit Testimonial Modal */}
      {editTestimonial && (
        <Modal show={showEditModal} onHide={() => setShowEditModal(false)} centered>
          <Modal.Header closeButton>
            <Modal.Title>Edit Testimonial</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <Form onSubmit={handleUpdate}>
              <Form.Group className="mb-3">
                <Form.Label>Name</Form.Label>
                <Form.Control
                  type="text"
                  name="name"
                  value={editTestimonial.name}
                  onChange={handleChange}
                  required
                />
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label>Testimonial Text</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={5}
                  name="text"
                  value={editTestimonial.text}
                  onChange={handleChange}
                  required
                />
              </Form.Group>
              <div className="text-center">
                <Button variant="primary" type="submit" className="me-2 modern-btn">
                  <i className="fas fa-check"></i> Update
                </Button>
                <Button variant="secondary" className="modern-btn" onClick={() => setShowEditModal(false)}>
                  <i className="fas fa-times"></i> Cancel
                </Button>
              </div>
            </Form>
          </Modal.Body>
        </Modal>
      )}
    </>
  );
};

export default TestimonialList;